/**
 * When a program runs, and when creators can join it.
 *
 * Every program has an end date. Most renew themselves a year at a time — a
 * shop's evergreen program should never lapse because nobody remembered to
 * extend it — so the end a gate checks is the EFFECTIVE end: the stored date
 * rolled forward by whole years while auto-extend is on. A background job
 * writes that rolled date back, but nothing waits for it, so a program can
 * never end in the gap between two runs of the job.
 *
 * Intake is either rolling (apply any time until the end) or a window that
 * closes before the program starts, for brands who pick their creators first.
 *
 * Pure: shared by the server's gates and the client's display.
 */

export const INTAKE_MODES = ['rolling', 'window'];

const asDate = (value) => (value ? new Date(value) : null);

/** The same calendar date `years` later (29 Feb lands on 1 Mar). */
export function addYears(date, years) {
  const next = new Date(date);
  next.setUTCFullYear(next.getUTCFullYear() + years);
  return next;
}

/** When the program starts counting: its start date, else when it was created. */
export function programStartsAt(program) {
  return asDate(program.startsAt) ?? asDate(program.createdAt);
}

/**
 * When the program ends, as of `now`. Auto-extending programs roll forward a
 * year at a time until the end is in the future. Null only for a program saved
 * before end dates existed, which is treated as still running until its next
 * save gives it one.
 */
export function effectiveEndsAt(program, now = new Date()) {
  let end = asDate(program.endsAt);
  if (!end || program.autoExtend === false) return end;
  const at = new Date(now);
  while (end <= at) end = addYears(end, 1);
  return end;
}

/** `upcoming` before the start, `ended` from the end on, else `running`. */
export function programPhase(program, now = new Date()) {
  const at = new Date(now);
  const start = programStartsAt(program);
  if (start && at < start) return 'upcoming';
  const end = effectiveEndsAt(program, at);
  if (end && at >= end) return 'ended';
  return 'running';
}

/**
 * Whether a creator can apply right now, and if not, why and since or until
 * when: `{ state: 'open' | 'not_yet' | 'closed', reason, at }`.
 */
export function intakeState(program, now = new Date()) {
  const at = new Date(now);
  const end = effectiveEndsAt(program, at);
  if (end && at >= end) return { state: 'closed', reason: 'ended', at: end };

  if (program.intake?.mode === 'window') {
    const opens = asDate(program.intake.opensAt);
    const closes = asDate(program.intake.closesAt);
    if (opens && at < opens) return { state: 'not_yet', reason: 'window', at: opens };
    if (closes && at >= closes) return { state: 'closed', reason: 'window', at: closes };
    return { state: 'open', reason: 'window', at: closes };
  }
  return { state: 'open', reason: 'rolling', at: end };
}

/** "3 November 2026" — dates in messages, unambiguous in every locale. */
export function formatDay(date) {
  return new Date(date).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** Why a creator cannot apply, in one sentence, or null when they can. */
export function intakeRefusal(program, now = new Date()) {
  const { state, reason, at } = intakeState(program, now);
  if (state === 'open') return null;
  if (reason === 'ended') return `This program ended on ${formatDay(at)}`;
  if (state === 'not_yet') return `Applications open on ${formatDay(at)}`;
  return `Applications closed on ${formatDay(at)}`;
}
